import { createFormHookContexts, useStore, type AnyFieldApi } from '@tanstack/react-form'

// Contexts live in ./contexts (no form-core import) so __root can mount the providers eagerly. Re-exported here.
export { FormReadOnlyProvider, useFormReadOnly, useFormOptionalLabel } from './contexts'

/**
 * Object.is equality: File-containing fields have zero own keys, so shallow equality never sees them change. The
 * `any` casts work around Derived/ReadonlyStore not satisfying useStore's AnyAtom across @tanstack/store versions.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useFieldValue<T>(field: { store: any; state: { value: T } }): T {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useStore(field.store, (state: any) => state.value, Object.is as any)
}

export function hasFieldError(meta: AnyFieldApi['state']['meta']): boolean {
  if (meta.isValid) return false
  const hasSubmitErrors = (meta.errorMap as Record<string, unknown[]> | undefined)?.onSubmit?.length
  return meta.isTouched || !!hasSubmitErrors
}

// TODO: We're passing field.state instead of field to avoid this bad behavior of tanstack form with react compiler:
// https://github.com/TanStack/form/issues/1379
export function FieldInfo(props: { fieldState: AnyFieldApi['state'] }) {
  const { meta } = props.fieldState
  if (meta.isValid) return null
  const hasSubmitErrors = (meta.errorMap as Record<string, unknown[]> | undefined)?.onSubmit?.length
  if (!meta.isTouched && !hasSubmitErrors) return null
  return (
    <p data-slot="form-message" className="text-destructive m-1 text-sm">
      {meta.errors.map((error: Error) => error.message).join(', ')}
    </p>
  )
}

export const { fieldContext, formContext, useFieldContext, useFormContext } = createFormHookContexts()

/**
 * Whether a resolved valibot schema represents a required field: an enum or number (no empty form of
 * them exists), or a pipeline carrying a `minLength`/`minValue`/`email`-style action that rejects the
 * empty case. valibot exposes its structure directly — `.type`, `.pipe`, `.wrapped`, `.entries`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function hasRequiredConstraint(schema: any): boolean {
  if (schema?.type === 'enum' || schema?.type === 'picklist' || schema?.type === 'number') return true

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const actions: any[] | undefined = schema?.pipe
  if (!Array.isArray(actions)) return false

  return actions.some((action) => {
    if (action?.type === 'min_length' || action?.type === 'min_size') return Number(action.requirement) >= 1
    if (action?.type === 'min_value') return Number(action.requirement) > 0
    // A format-constrained string (email, url, iso_date) is inherently non-empty.
    return action?.type === 'email' || action?.type === 'url' || action?.type === 'iso_date'
  })
}

/**
 * Walk a valibot schema by dot/bracket path and say whether the target field is optional.
 * `true` optional, `false` required, `undefined` when the path cannot be resolved.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isOptionalInSchema(schema: any, fieldPath: string): boolean | undefined {
  const parts = fieldPath.replace(/\[(\d+)\]/g, '.$1').split('.')
  let current = schema

  for (const part of parts) {
    if (!current?.type) return undefined
    // A pipeline wraps the schema it validates; step through to the object/array underneath.
    while (current?.type === 'optional' || current?.type === 'nullish' || current?.type === 'nullable') {
      current = current.wrapped
    }

    if ((current?.type === 'object' || current?.type === 'loose_object') && current.entries) {
      current = current.entries[part]
    } else if (current?.type === 'array' && /^\d+$/.test(part)) {
      current = current.item
    } else if (current?.type === 'record') {
      current = current.value
    } else {
      return undefined
    }
  }

  if (!current?.type) return undefined
  // `optional`/`nullish` wrappers are the explicit "may be absent" marker.
  if (current.type === 'optional' || current.type === 'nullish') return true

  // A union written to allow clearing — `File | '' | undefined` — is optional, while `File | undefined`
  // is NOT: there, undefined means "no change", not "no value". Same rule the zod version encoded.
  if (current.type === 'union' && Array.isArray(current.options)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const options: any[] = current.options
    const hasUndefined = options.some((o) => o?.type === 'undefined')
    const hasLiteralEmpty = options.some((o) => o?.type === 'literal' && o?.literal === '')
    if (hasUndefined) return hasLiteralEmpty
  }

  return !hasRequiredConstraint(current)
}

/** Falls back to the field-level validator when the form has no Zod schema. */
export function useIsFieldOptional(fieldName: string, explicitOptional?: boolean): boolean {
  const field = useFieldContext()
  if (explicitOptional !== undefined) return explicitOptional

  const validators = field.form.options.validators
  const formSchema =
    validators?.onSubmit ??
    validators?.onChange ??
    validators?.onBlur ??
    (validators as Record<string, unknown>)?.onChangeAsync ??
    (validators as Record<string, unknown>)?.onSubmitAsync ??
    (validators as Record<string, unknown>)?.onBlurAsync ??
    (validators as Record<string, unknown>)?.onDynamic
  if (formSchema) {
    const result = isOptionalInSchema(formSchema, fieldName)
    if (result !== undefined) return result
  }

  const fieldValidators = field.options.validators as Record<string, unknown> | undefined
  return !(
    fieldValidators?.onSubmit ??
    fieldValidators?.onChange ??
    fieldValidators?.onBlur ??
    fieldValidators?.onChangeAsync ??
    fieldValidators?.onSubmitAsync ??
    fieldValidators?.onBlurAsync
  )
}
